/**
 * Roster data access — planning_resources / planning_role_assignments (mig 687).
 *
 * The pure derivation lives in planningRoster.ts; this is the IO shell.
 */

import { getSupabaseClient } from './supabaseClient';
import type {
  PlanningResource,
  PlanningResourceKind,
  PlanningRoleAssignment,
} from './planningRoster';

const TABLE_RESOURCES = 'planning_resources';
const TABLE_ASSIGNMENTS = 'planning_role_assignments';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toResource(row: Record<string, any>): PlanningResource {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    userId: row.user_id ?? null,
    displayName: row.display_name,
    kind: (row.kind ?? 'person') as PlanningResourceKind,
    active: row.active ?? true,
    notes: row.notes ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toAssignment(row: Record<string, any>): PlanningRoleAssignment {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    resourceId: row.resource_id,
    resourceType: row.resource_type,
    hoursPerWeek: Number(row.hours_per_week ?? 0),
    orgUnitId: row.org_unit_id ?? null,
    effectiveFrom: row.effective_from ?? null,
    effectiveTo: row.effective_to ?? null,
    dedicatedToPriorityId: row.dedicated_to_priority_id ?? null,
    notes: row.notes ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------

export async function getPlanningResources(workspaceId: string): Promise<PlanningResource[]> {
  const { data, error } = await getSupabaseClient()
    .from(TABLE_RESOURCES)
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('display_name', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(toResource);
}

export async function createPlanningResource(params: {
  workspaceId: string;
  displayName: string;
  kind?: PlanningResourceKind;
  userId?: string | null;
  notes?: string | null;
}): Promise<PlanningResource> {
  const { data, error } = await getSupabaseClient()
    .from(TABLE_RESOURCES)
    .insert({
      workspace_id: params.workspaceId,
      display_name: params.displayName.trim(),
      kind: params.kind ?? 'person',
      user_id: params.userId ?? null,
      notes: params.notes ?? null,
    })
    .select('*')
    .single();
  if (error) throw error;
  return toResource(data as Record<string, unknown>);
}

export async function updatePlanningResource(
  id: string,
  updates: Partial<Pick<PlanningResource, 'displayName' | 'kind' | 'active' | 'notes'>>,
): Promise<PlanningResource> {
  const payload: Record<string, unknown> = {};
  if (updates.displayName !== undefined) payload.display_name = updates.displayName.trim();
  if (updates.kind !== undefined) payload.kind = updates.kind;
  if (updates.active !== undefined) payload.active = updates.active;
  if (updates.notes !== undefined) payload.notes = updates.notes;
  const { data, error } = await getSupabaseClient()
    .from(TABLE_RESOURCES)
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return toResource(data as Record<string, unknown>);
}

export async function deletePlanningResource(id: string): Promise<void> {
  const { error } = await getSupabaseClient().from(TABLE_RESOURCES).delete().eq('id', id);
  if (error) throw error;
}

/**
 * Makes sure every workspace member has a `person` resource row.
 *
 * The roster surface already lists workspace members with hours and
 * capabilities; this gives each of them the planning-side identity that role
 * assignments hang off, without asking anyone to re-enter their own team.
 * Idempotent — existing rows are left exactly as they are.
 */
export async function ensureResourcesForUsers(
  workspaceId: string,
  members: Array<{ userId: string; name: string }>,
): Promise<PlanningResource[]> {
  const existing = await getPlanningResources(workspaceId);
  const known = new Set(existing.map((r) => r.userId).filter(Boolean) as string[]);
  const missing = members.filter((m) => !known.has(m.userId));
  if (missing.length === 0) return existing;

  const { data, error } = await getSupabaseClient()
    .from(TABLE_RESOURCES)
    .insert(
      missing.map((m) => ({
        workspace_id: workspaceId,
        user_id: m.userId,
        display_name: m.name,
        kind: 'person',
      })),
    )
    .select('*');
  if (error) throw error;
  return [...existing, ...(data ?? []).map(toResource)].sort((a, b) =>
    a.displayName.localeCompare(b.displayName),
  );
}

// ---------------------------------------------------------------------------
// Role assignments
// ---------------------------------------------------------------------------

export async function getRoleAssignments(workspaceId: string): Promise<PlanningRoleAssignment[]> {
  const { data, error } = await getSupabaseClient()
    .from(TABLE_ASSIGNMENTS)
    .select('*')
    .eq('workspace_id', workspaceId);
  if (error) throw error;
  return (data ?? []).map(toAssignment);
}

export async function createRoleAssignment(params: {
  workspaceId: string;
  resourceId: string;
  resourceType: string;
  hoursPerWeek: number;
  orgUnitId?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  notes?: string | null;
}): Promise<PlanningRoleAssignment> {
  const { data, error } = await getSupabaseClient()
    .from(TABLE_ASSIGNMENTS)
    .insert({
      workspace_id: params.workspaceId,
      resource_id: params.resourceId,
      resource_type: params.resourceType,
      hours_per_week: params.hoursPerWeek,
      org_unit_id: params.orgUnitId ?? null,
      effective_from: params.effectiveFrom ?? null,
      effective_to: params.effectiveTo ?? null,
      notes: params.notes ?? null,
    })
    .select('*')
    .single();
  if (error) throw error;
  return toAssignment(data as Record<string, unknown>);
}

export async function updateRoleAssignment(
  id: string,
  updates: Partial<
    Pick<
      PlanningRoleAssignment,
      | 'resourceType'
      | 'hoursPerWeek'
      | 'orgUnitId'
      | 'effectiveFrom'
      | 'effectiveTo'
      | 'dedicatedToPriorityId'
      | 'notes'
    >
  >,
): Promise<PlanningRoleAssignment> {
  const payload: Record<string, unknown> = {};
  if (updates.resourceType !== undefined) payload.resource_type = updates.resourceType;
  if (updates.hoursPerWeek !== undefined) payload.hours_per_week = updates.hoursPerWeek;
  if (updates.orgUnitId !== undefined) payload.org_unit_id = updates.orgUnitId;
  if (updates.effectiveFrom !== undefined) payload.effective_from = updates.effectiveFrom;
  if (updates.effectiveTo !== undefined) payload.effective_to = updates.effectiveTo;
  if (updates.dedicatedToPriorityId !== undefined) {
    payload.dedicated_to_priority_id = updates.dedicatedToPriorityId;
  }
  if (updates.notes !== undefined) payload.notes = updates.notes;

  const { data, error } = await getSupabaseClient()
    .from(TABLE_ASSIGNMENTS)
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return toAssignment(data as Record<string, unknown>);
}

export async function deleteRoleAssignment(id: string): Promise<void> {
  const { error } = await getSupabaseClient().from(TABLE_ASSIGNMENTS).delete().eq('id', id);
  if (error) throw error;
}

/**
 * Everything a run needs from the roster, in one round trip.
 *
 * Returns empty arrays rather than throwing when the tables are unreachable:
 * a roster read failure must degrade a roster-mode scenario to its manual pools,
 * never break the run outright.
 */
export async function getRosterSupply(workspaceId: string): Promise<{
  resources: PlanningResource[];
  assignments: PlanningRoleAssignment[];
}> {
  try {
    const [resources, assignments] = await Promise.all([
      getPlanningResources(workspaceId),
      getRoleAssignments(workspaceId),
    ]);
    return { resources, assignments };
  } catch (err) {
    console.warn('[getRosterSupply] roster unavailable; falling back to manual pools', err);
    return { resources: [], assignments: [] };
  }
}
